export enum SortMethod {
  POPULARITY = "popularity",
  PRICE_ASC = "price_asc",
  PRICE_DESC = "price_desc",
  SIZE_ASC = "size_asc",
  SIZE_DESC = "size_desc",
}

export enum LandType {
  VACANT_BLOCK = "build",
  HOUSE_LAND_PACKAGE = "package",
  KNOCK_DOWN_REBUILD = "rebuild",
}

export enum HouseType {
  SINGLE_STORY = "single-storey",
  DOUBLE_STORY = "double-storey",
  ACREAGE = "acreage",
}

export enum BuildSituation {
  FIRST_HOME_BUYER = "First Home Buyer",
  KNOCK_DOWN_REBUILD = "Knock Down Rebuild / Upsizing",
  INVESTOR = "Investor",
}

export enum RoutePath {
  HOME = "/",
  LOGIN = "/login",
  YOUR_LAND = "/your-land",
  CHOOSE_FLOOR_PLAN = "/floorplan",
  CHOOSE_STREET_APPEAL = "/facades",
  CHOOSE_EXTERIOR_STYLE = "/exterior-style",
  PROMOTIONS = "/promotions",
  PERSONALISE_HOME = "/personalise-home",
  SIGN_UP = "/sign-up",
  UPGRADE_OPTIONS = "/upgrade-options",
  TURN_KEY_INCLUSIONS = "/turn-key-inclusions",
  // KITCHEN_STYLE = "/kitchen-style", // Kitchen style removed by client request 5/5/22
  INTERIOR_STYLE = "/interior-style",
  LIFESTYLE_OPTIONS = "/lifestyle-options",
  SPECIAL_OFFERS = "/special-offers",
  FINAL_DETAILS = "/final-details",
  YOUR_NEW_HOME = "/your-new-home",
  PACKAGE_SEARCH = "/packages",
  PACKAGE_SEARCH_RESULTS = "/packages/results",
  PACKAGE_DETAILS = "/package-details",
  SIMPLE_DETAILS = "/welcome",
  THANK_YOU = "/thank-you",
}

export // Prefixing with 'state-' as there is a CSS collision with October styles
type ExpandState =
  | "state-collapsed"
  | "state-collapsing"
  | "state-expanding"
  | "state-expanded";

export enum IconName {
  // Note: Don't use spaces in the enum value - it will be used as part of the icon's class name in HTML
  ARROW = "arrow",
  ARROW_THIN = "arrow-thin",
  BATH = "bath",
  BED = "bed",
  BLUEPRINT = "blueprint",
  CAR = "car",
  CHECK_MARK = "check-mark",
  CHECK_MARK_CIRCLE = "check-mark-circle",
  CHECK_MARK_CIRCLE_OUTLINE = "check-mark-circle-outline",
  CHEVRON_DOWN = "chevron-down",
  CHEVRON_UP_WHITE = "chevron-up-white",
  CLOSE = "close",
  COUCH = "couch",
  DIAMOND = "diamond",
  DISCOUNT = "discount",
  FAN = "fan",
  FANCYBOX_CLOSE = "fancybox-close",
  FLIP = "flip",
  FULLSCREEN = "fullscreen",
  HOUSE_ACREAGE = "house-acreage",
  HOUSE_DOUBLE_STORY = "house-double-story",
  HOUSE_KEYS = "house-keys",
  HOUSE_LAND_PACKAGE = "house-land-package",
  HOUSE_OUTLINE = "house-outline",
  HOUSE_SINGLE_STORY = "house-single-story",
  KITCHEN = "kitchen",
  KNOCK_DOWN_REBUILD = "knock-down-rebuild",
  LOCATION = "location",
  PARQUET = "parquet",
  PLANT = "plant",
  RESTART = "restart",
  SEARCH = "search",
  SHARE_FACEBOOK = "share-faceboook",
  SHARE_MESSENGER = "share-messenger",
  SHARE_EMAIL = "share-email",
  SHARE_MESSAGE = "share-message",
  SHARE_LINK = "share-link",
  SHARE_PDF = "share-pdf",
  SPEECH_BUBBLE = "speech-bubble",
  THREE_D = "3d",
  TILES = "tiles",
  TIMES = "times",
  TOUCH = "touch",
  UNDO = "undo",
  LAND_SITUATION_NSW_DESKTOP_VACANT = "Currently own a vacant block of land",
  LAND_SITUATION_NSW_DESKTOP_LOOKING = "Looking to purchase a vacant block of land",
  LAND_SITUATION_NSW_DESKTOP_KDR = "Planning a knockdown rebuild project",
  LAND_SITUATION_NSW_DESKTOP_HL = "Interest in House & Land Packages",
  ENVELOPE = "envelope",
  LOT_WIDTH = "lot-width",
}

export type Estate = {
  id: string;
  title: string;
  site_cost: number | null;
};

export type Region = {
  id: string;
  title: string;
  estates: Estate[];
};

export type Consultant = {
  id: string;
  name: string;
};

export type DisplayLocation = {
  id: string;
  title: string;
};

export type CustomiserSwitchOption = {
  id: number;
  included: boolean;
  default: boolean;
  label: string;
  price: number;
};
export interface FloorplanOption extends CustomiserSwitchOption {
  svg_id: string;
  conflicts: number[];
}
export interface FacadeFinishOption extends CustomiserSwitchOption {
  rendivo_id: string; // Rendivo 'FINISH_THEME_ID'
  rendivo_tag: string;
}

export type Facade = {
  id: number;
  rendivo_id: string; // Rendivo 'PRODUCT_ID'
  rendivo_levels: number;
  title: string;
  base_price: number;
  finish_options: FacadeFinishOption[];
};

export interface Home {
  id: number;
  house_type: HouseType;
  sort_order: number;
  virtual_tour_url?: string;
  floorplan_combined: string;
  floorplan_combined_flip: string;
  floorplan_lower: string;
  floorplan_lower_flip: string;
  floorplan_upper: string;
  floorplan_upper_flip: string;
  floorplan_image: string;
  flip_css: boolean;
  title: string;
  price: number;
  total_area_m: number;
  total_area_sq: number;
  lot_width: number;
  width: string;
  length: string;
  bedrooms: number;
  living_areas: number;
  bathrooms: number;
  car_spaces: number;
  storeys: number;
  floorplan_options: FloorplanOption[];
  facades: Facade[];
  inclusions: Inclusion[];
  turn_key_inclusions: Inclusion[];
  upgrades: Upgrade[];
  upgrade_category: UpgradeCategory[];
  lifestyle_options: Upgrade[];
  lifestyle_option_category: UpgradeCategory[];
  promotions: Promotion[];
  acreage: boolean;
  narrow: boolean;
  vpb_pkid: number;
  slug: string;
  roof_upgrades: Record<number, number>;
  flooring_upgrades: Record<number, number>;
  kitchen_upgrades: Record<number, number>;
}

export interface HouseLand extends Home {
  home_id: number;
  house_design: string;
  address: string;
  suburb: string;
  state: string;
  postcode: string;
  lot_size: number;
  gallery_images: string[];
  listing_image: string;
  exterior_colour: string;
  facade_title: string;
  facade_finish: string;
  inclusions_copy: string;
  latitude: number;
  longitude: number;
  estate: string;
  storeys: number;
  land_cost: number;
  site_cost: number;
  facade_price: number;
  inclusion_price: number;
  turn_key_price: number;
  lifestyle_price: number;
  rendivo_id: string; // Rendivo Product ID
  rendivo_levels: number;
  rendivo_theme_id: string; // Rendivo Theme ID
  pdf_fixed_price: boolean;
}

export type UpgradeStyle = {
  id: number;
  rendivo_id: string;
  fallback_image?: string;
  thumbnail: string;
  title: string;
  price: number;
};

export type ExteriorStyle = {
  id: number;
  thumbnail: string;
  title: string;
  price: number;
  rendivo_id: string;
  rendivo_tag: string;
  recommended_interior_colour_id: number | null;
  roof_options: UpgradeStyle[];
  window_options: UpgradeStyle[];
  gutter_options: UpgradeStyle[];
};

export type InteriorStyle = {
  id: number;
  thumbnail: string;
  title: string;
  price: number;
  rendivo_id: string;
  slider_items: {
    type: "kitchen" | "bedroom" | "bathroom";
    title: string;
    rendivo_id: string | null;
    image: string | null;
  }[];
  kitchen_options: UpgradeStyle[];
  flooring_options: UpgradeStyle[];
};
export interface InclusionSectionContent {
  image: string;
  title: string;
  copy: string;
  image_overrides?: Record<number, string>; // Only used by upgrades
}

export interface InclusionSection {
  id: number;
  image: string;
  image_overrides?: Record<number, string>; // Only used by upgrades
  title: string;
  copy: string;
  content: InclusionSectionContent[];
}

export interface Inclusion {
  id: number;
  image: string;
  title: string;
  price: number;
  preselected: boolean;
  promo_price?: number;
  content: string;
  blurb: string;
  sections: InclusionSection[];
  kitchen_rendivo_id?: string | null;
  image_overrides?: Record<number, string>;
}

export interface Upgrade extends Inclusion {
  // Is the exact same as LifestyleOption
  category_id: number;
  compare_table: CompareRow[];
}

export interface CompareRow {
  title: string;
  optional: boolean;
  items: string[];
}

export type UpgradeCategory = {
  // Is the exact same as LifestyleOptionCategory
  id: number;
  title: string;
};

export interface PromotionSection extends InclusionSection {
  tab_title: string;
}

export type Promotion = {
  id: number;
  thumbnail: string;
  background_image_desktop: string;
  background_image_mobile: string;
  preselected: boolean;
  title: string;
  amount: number;
  type:
    | "perc_off_home"
    | "dollar_off_home"
    | "dollar_off_home_via_pivot"
    | "no_additional_cost"
    | "price_reduction"
    | "dollar_amount_bonus"
    | "dollar_amount_bonus_plus_floorplan_options"
    | "dollar_off_home_plus_no_additional_cost";
  blurb: string;
  sections: PromotionSection[];
  upgrades: Record<number, number>; // Mapping of ID to price override amount
  inclusions: Record<number, number>;
  lifestyle_options: Record<number, number>;
  turn_key_inclusions: Record<number, number>;
  show_popout: boolean;
  popout_copy: string;
  estimate_copy: string;
  expiry_date: string;
};

export type BreadcrumbItem = {
  icon: IconName;
  title: string;
  routes: string[];
};

export type User = {
  name: string;
  surname: string;
  phone: string;
  email: string;
  opt_in: boolean;
  password?: string;
  password_confirmation?: string;
  consultant?: Consultant | null;
  nearest_display?: DisplayLocation | null;
  remember?: boolean;
  region?: Region | null;
  email_secondary?: string;
  lead_status?: string | null;
  land_situation?: string | null;
  build_situation?: BuildSituation | null;
};

export type SessionState = {
  region: Region | null;
  estate: Estate | null;
  estate_other: string;
  lot_width: string;
  house_type: HouseType | null;
  build_situation: BuildSituation | null;
  current_route: RoutePath;
  current_step: number;
  title: string;
  perc_complete: number;
  total_price: number;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  email_secondary: string;
  opt_in: boolean;
  apply_for_grant: boolean | null;
  grant_settings: {
    grant_enabled: boolean;
    grant_label: string;
    grant_name: string;
    grant_amount: number;
  };
  financial_situation: string | null;
  build_timeline: string | null;
  land_situation: string | null;
  lead_status: string | null;
  land_registration_date: string | null;
  final_comments: string | null;
  consultant_id: string | null;
  remember_details: boolean;
  land_type: LandType | null;
  home_id: number | null;
  house_land_id: number | null;
  is_floorplan_flipped: boolean;
  floorplan_ids: number[];
  facade_id: number | null;
  facade_finish_id: number | null;
  exterior_style_id: number | null;
  roof_style_id: number | null;
  window_style_id: number | null;
  gutter_style_id: number | null;
  rendivo_style: string | null;
  facade_rendivo_id: string | null;
  facade_finish_rendivo_id: string | null;
  exterior_style_rendivo_id: string | null;
  roof_style_rendivo_id: string | null;
  window_style_rendivo_id: string | null;
  gutter_style_rendivo_id: string | null;
  inclusion_id: number | null;
  turn_inclusion_id: number | null;
  upgrade_ids: Record<number, number>;
  lifestyle_option_ids: Record<number, number[]>;
  interior_style_id: number | null;
  flooring_style_id: number | null;
  kitchen_style_id: number | null;
  promotion_id: number | null;
  special_offer_id: number | null;
  send_to_finance_manager: boolean;
  interested_in_granny_flats: boolean;
  consent: boolean;
  final_details_settings: {
    promo_enabled: boolean;
    promo_desktop_image: string;
    promo_mobile_image: string;
    promo_link: string;
    promo_link_text: string;
    further_customise_enabled: boolean;
    further_customise_copy: string;
    thank_you_enabled: boolean;
    thank_you_copy: string;
    disclaimer_copy: string;
    kiosk_footer_copy: string;
    kiosk_thank_you_copy: string;
    print_logo: string;
    form_disclaimer: string;
    finance_cta_title: string;
  };
  price_disclaimers: string[];
  finance_cta_button: Button | null;
};

export type Button = {
  link: string;
  text: string;
  external: boolean;
};

export type Series = {
  id: string;
  title: string;
  storeys: number;
  acreage: boolean;
  homes: Home[] | null;
};

export type ProcessStep = {
  title: string;
  copy: string;
};